import React, { useMemo, useState } from 'react';
import {
    LayoutDashboard,
    Newspaper,
    Home,
    Users,
    BarChart3,
    MessageSquare,
    Calendar,
    LogOut,
    Sparkles,
    X,
    Building2,
    Globe,
    Handshake,
    Network,
    LayoutTemplate,
    ClipboardList,
    ShieldAlert,
    ChevronDown,
    Crown,
    BookOpen,
    CreditCard,
    TrendingUp,
    Flag,
    Settings,
    LifeBuoy,
    Lightbulb,
    Landmark
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';
import { useAuth } from '../../contexts/AuthContext';

interface SidebarProps {
    activeView: string;
    onViewChange: (view: string) => void;
    // Badge counts keyed by menu id. Computed once in AppLayout, which also
    // totals them on the bell, so the sidebar and the bell never disagree.
    badges: Record<string, number>;
    // Optional prefetch hook fired on hover/focus of a nav item. AppLayout
    // wires this to kick off the underlying data fetch (e.g. fetchProperties
    // with the user's stored filters) so the destination page has data ready
    // by the time the click lands. No-op if not provided.
    onPrefetchView?: (view: string) => void;
    isOpen: boolean;
    onClose: () => void;
    theme: 'light' | 'dark' | 'high-contrast';
    isCollapsed?: boolean;
    onToggleCollapse?: () => void;
}

const EASING = 'transition-all duration-[350ms] ease-[cubic-bezier(0.4,0,0.2,1)]';
const COLLAPSE_KEY = 'intver2.sidebar.collapsed_sections';

// Per-section color palette. Each section's items always carry the section's
// background + text tint (matching how Panel Control's active state looks);
// the active item just escalates to a stronger bg + a ring. Standalone items
// (Panel Control, super-admin tail) get no palette and fall back to the
// user's theme accent (--accent-*).
//
// Light tones map to Tailwind's c50 (rest) and c100 (active); dark tones use
// rgba over the dark surface at two alpha steps.
type Palette = {
    bgRestLight: string;    // Tailwind c50 hex
    bgActiveLight: string;  // Tailwind c100 hex
    bgRestDark: string;     // low-alpha rgba of c500
    bgActiveDark: string;   // higher-alpha rgba of c500
    c500: string;           // icon, dot, badge, header marker
    c600: string;           // light-mode text
    c400: string;           // dark-mode text
    glow: string;           // ring on active
};
const SECTION_PALETTE: Record<string, Palette> = {
    operacion: {
        bgRestLight: '#ecfdf5',
        bgActiveLight: '#d1fae5',
        bgRestDark: 'rgba(16,185,129,0.06)',
        bgActiveDark: 'rgba(16,185,129,0.16)',
        c500: '#10b981',
        c600: '#059669',
        c400: '#34d399',
        glow: 'rgba(16,185,129,0.3)',
    },
    intercambio: {
        bgRestLight: '#f5f3ff',
        bgActiveLight: '#ede9fe',
        bgRestDark: 'rgba(139,92,246,0.06)',
        bgActiveDark: 'rgba(139,92,246,0.16)',
        c500: '#8b5cf6',
        c600: '#7c3aed',
        c400: '#a78bfa',
        glow: 'rgba(139,92,246,0.3)',
    },
    'mi-agencia': {
        bgRestLight: '#fffbeb',
        bgActiveLight: '#fef3c7',
        bgRestDark: 'rgba(245,158,11,0.06)',
        bgActiveDark: 'rgba(245,158,11,0.16)',
        c500: '#f59e0b',
        c600: '#d97706',
        c400: '#fbbf24',
        glow: 'rgba(245,158,11,0.3)',
    },
    comunidad: {
        bgRestLight: '#f0f9ff',
        bgActiveLight: '#e0f2fe',
        bgRestDark: 'rgba(14,165,233,0.06)',
        bgActiveDark: 'rgba(14,165,233,0.16)',
        c500: '#0ea5e9',
        c600: '#0284c7',
        c400: '#38bdf8',
        glow: 'rgba(14,165,233,0.3)',
    },
};

type MenuItem = { id: string; label: string; icon: any; badge?: number };
type MenuSection = { id: string; label: string; items: MenuItem[] };

export const Sidebar: React.FC<SidebarProps> = ({
    activeView,
    onViewChange,
    badges,
    onPrefetchView,
    isOpen,
    onClose,
    theme,
    isCollapsed = false,
}) => {
    const isLight = theme !== 'dark';
    const { isSuperAdmin, isSuspended, signOut, profile, realProfile, agency } = useAuth();
    // Agency-level kill-switch (Super Admin > Agencias > Compartir). Hides
    // the entire Intercambio nav group when the agency owner has disabled
    // sharing — see set_agency_can_share RPC. Super admins always see it.
    const sharingDisabled = !isSuperAdmin && agency?.can_share === false;

    const messagesUnread = badges.messages ?? 0;
    const openTickets = badges.admin_tickets ?? 0;
    const pendingCompliance = badges.admin_cumplimiento ?? 0;
    const newSugerencias = badges.sugerencias ?? 0;
    const pendingBlog = badges.admin_contenido ?? 0;

    // Collapsed-rail tooltip. Native `title` has a ~1s delay and can't be
    // styled; when the rail is icon-only we show our own instant pill. Portaled
    // to <body> because the aside is overflow-hidden AND (via translate-x) a
    // containing block, so anything positioned inside it is clipped to the rail.
    const [tip, setTip] = useState<{ label: string; top: number } | null>(null);
    const showTip = (e: React.MouseEvent<HTMLElement>, label: string) => {
        if (!isCollapsed) return;
        const r = e.currentTarget.getBoundingClientRect();
        setTip({ label, top: r.top + r.height / 2 });
    };
    const hideTip = () => setTip(null);

    // Per-section collapse state. All sections open by default — the PDF guide
    // points users at "Menú lateral → MI AGENCIA → Página Web", so that group
    // must be visible without a toggle. User toggles persist to localStorage
    // so the layout sticks across sessions.
    const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>(() => {
        try {
            const stored = localStorage.getItem(COLLAPSE_KEY);
            if (stored) return JSON.parse(stored);
        } catch { /* noop */ }
        return {};
    });

    const toggleSection = (id: string) => {
        setCollapsedSections(prev => {
            const next = { ...prev, [id]: !prev[id] };
            try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify(next)); } catch { /* noop */ }
            return next;
        });
    };

    const { topItems, sections, bottomItems } = useMemo(() => {
        // Suspended agencies only see Mi Membresía so they can renew.
        if (isSuspended) {
            return {
                topItems: [] as MenuItem[],
                sections: [
                    { id: 'mi-agencia', label: 'Mi Agencia', items: [
                        { id: 'membresia', label: 'Mi Membresía', icon: Crown },
                    ]},
                ] as MenuSection[],
                bottomItems: [] as MenuItem[],
            };
        }

        // ── Super admin gets a purpose-built, admin-first menu — NOT the agency
        // nav with a tail. The platform sections map to /administrador/<section>
        // routes; the read-only "Supervisión" group reuses the agency browse
        // views (RLS gives super_admin a global view). Sales-scoped admins lose
        // the ops/config sections (mirrors showFullAdminTabs in SuperAdminView).
        if (isSuperAdmin) {
            const isSalesScope = realProfile?.super_admin_scope === 'sales';
            const plataforma: MenuItem[] = [
                { id: 'admin_agencias', label: 'Agencias', icon: Building2 },
                { id: 'admin_usuarios', label: 'Usuarios', icon: Users },
                { id: 'admin_facturacion', label: 'Facturación', icon: CreditCard },
                { id: 'admin_ventas', label: 'Prospectos SaaS', icon: TrendingUp },
                { id: 'admin_cumplimiento', label: 'Cumplimiento', icon: Flag, badge: pendingCompliance || undefined },
            ];
            if (!isSalesScope) {
                // Badge = agency blog posts waiting for review (Blog tab).
                plataforma.push({ id: 'admin_contenido', label: 'Contenido y Portales', icon: Globe, badge: pendingBlog || undefined });
                plataforma.push({ id: 'admin_sistema', label: 'Sistema', icon: Settings });
            }
            return {
                topItems: [{ id: 'admin_resumen', label: 'Resumen', icon: LayoutDashboard }] as MenuItem[],
                sections: [
                    { id: 'plataforma', label: 'Plataforma', items: plataforma },
                    { id: 'supervision', label: 'Supervisión · solo lectura', items: [
                        { id: 'properties', label: 'Propiedades', icon: Home },
                        { id: 'leads', label: 'Prospectos', icon: Users },
                        { id: 'messages', label: 'Mensajes', icon: MessageSquare, badge: messagesUnread || undefined },
                        { id: 'admin_tickets', label: 'Tickets', icon: LifeBuoy, badge: openTickets || undefined },
                        // Not read-only for the admin: this is where they set
                        // each idea's roadmap status (same page as the agency nav).
                        // Badge counts status='new' — it clears when the admin
                        // moves the suggestion to any roadmap status.
                        { id: 'sugerencias', label: 'Sugerencias', icon: Lightbulb, badge: newSugerencias || undefined },
                    ]},
                ] as MenuSection[],
                bottomItems: [] as MenuItem[],
            };
        }

        const top: MenuItem[] = [
            { id: 'dashboard', label: 'Panel Control', icon: LayoutDashboard },
            { id: 'novedades', label: 'Novedades', icon: Newspaper },
        ];

        const operacion: MenuItem[] = [
            { id: 'properties', label: 'Propiedades', icon: Home },
            { id: 'leads', label: 'Prospectos', icon: Users },
            { id: 'requerimientos', label: 'Requerimientos', icon: ClipboardList },
            { id: 'tasks', label: 'Propietarios', icon: Calendar },
            { id: 'projects', label: 'Proyectos', icon: Building2 },
        ];
        // Omnichannel inbox (WhatsApp/Instagram/Facebook/email). Agency-scoped
        // for admins/agents; super admins reach the platform-wide view via the
        // utilities tail below instead.
        if (!isSuperAdmin) operacion.push({ id: 'messages', label: 'Mensajes', icon: MessageSquare, badge: messagesUnread || undefined });
        // Reportería is agency-scoped (agents see their own, admins see all).
        // Super admins have the separate platform "Análisis" below instead.
        if (!isSuperAdmin) operacion.push({ id: 'reportes', label: 'Reportes', icon: BarChart3 });
        // Banco G&T Continental applications for clients. Super admins have the
        // platform-wide list under Ventas instead (and no agency to file from).
        if (!isSuperAdmin) operacion.push({ id: 'financiamiento', label: 'Financiamiento', icon: Landmark });

        const intercambio: MenuItem[] = [
            ...(sharingDisabled ? [] : [
                { id: 'coincidencias', label: 'Coincidencias', icon: Sparkles },
                { id: 'exchange_properties', label: 'Intercambio Propiedades', icon: Globe },
                { id: 'exchange_requirements', label: 'Intercambio Requerimientos', icon: Handshake },
                // Intercambio Mensajes now lives inside the unified Mensajes
                // inbox (source filter "Intercambio") — no separate entry.
                { id: 'colegas', label: 'Colegas', icon: Network },
            ] as MenuItem[]),
            // Writing for the network blog is open to every agency, sharing on
            // or off — so it sits outside the can_share gate above.
            { id: 'blog_inmobiliario', label: 'Blog Inmobiliario', icon: Newspaper },
        ];

        const miAgencia: MenuItem[] = [];
        if (!isSuperAdmin) miAgencia.push({ id: 'membresia', label: 'Mi Membresía', icon: Crown });
        if (profile?.role === 'agency_admin' || isSuperAdmin) miAgencia.push({ id: 'equipo', label: 'Mi Equipo', icon: ShieldAlert });
        miAgencia.push({ id: 'reglamento', label: 'Reglamento', icon: BookOpen });
        // Only the admin edits the site (owner decision 2026-10-31); an agent
        // with "Escribir en blog" still gets the page, reduced to the Blog tab.
        if (profile?.role === 'agency_admin' || isSuperAdmin || profile?.can_write_blog) {
            miAgencia.push({ id: 'website', label: 'Página Web', icon: LayoutTemplate });
        }
        // Super admins have the platform-wide Tickets view instead — and the
        // create RPC rejects them anyway (they have no agency_id).
        if (!isSuperAdmin) miAgencia.push({ id: 'soporte', label: 'Soporte', icon: LifeBuoy });

        // Super-admin-only utilities live below Mi Agencia, separated by a
        // hairline so they don't compete with the user's day-to-day groups.
        const bottom: MenuItem[] = isSuperAdmin ? [
            { id: 'analytics', label: 'Análisis', icon: BarChart3 },
            { id: 'messages', label: 'Mensajes', icon: MessageSquare, badge: messagesUnread || undefined },
            { id: 'superadmin', label: 'Panel SaaS', icon: ShieldAlert },
        ] : [];

        // Comunidad is platform-wide (NOT can_share-gated like Intercambio):
        // every agency votes on the same product board.
        const comunidad: MenuItem[] = [
            { id: 'sugerencias', label: 'Sugerencias', icon: Lightbulb },
        ];

        const allSections: MenuSection[] = [
            { id: 'operacion', label: 'Operación', items: operacion },
            { id: 'intercambio', label: 'Red Intercambio', items: intercambio },
            { id: 'comunidad', label: 'Comunidad', items: comunidad },
            { id: 'mi-agencia', label: 'Mi Agencia', items: miAgencia },
        ].filter(s => s.items.length > 0);

        return { topItems: top, sections: allSections, bottomItems: bottom };
    }, [isSuspended, isSuperAdmin, profile?.role, profile?.can_write_blog, realProfile?.super_admin_scope, sharingDisabled, messagesUnread, openTickets, pendingCompliance, newSugerencias, pendingBlog]);

    const renderItem = (item: MenuItem, palette?: Palette) => {
        const isActive = activeView === item.id ||
            (item.id === 'leads' && (activeView === 'archived-leads' || activeView === 'database-leads' || activeView === 'negociacion-leads')) ||
            (item.id === 'tasks' && (activeView === 'archived-tasks' || activeView === 'database-tasks' || activeView === 'negociacion-tasks'));

        // Items inside a colored section inherit that palette. The bg tint
        // is applied at the SECTION level (one continuous surface), not per
        // item — items themselves stay transparent at rest so they read as
        // part of the same surface. Hover/active escalate to a deeper tint.
        // Standalone items (Panel Control + super-admin tail) get no palette
        // and fall back to the user's theme accent.
        const c500 = palette?.c500 ?? 'var(--accent-500)';
        const c600 = palette?.c600 ?? 'var(--accent-600)';
        const c400 = palette?.c400 ?? 'var(--accent-400)';
        const activeBg = palette ? (isLight ? palette.bgActiveLight : palette.bgActiveDark) : (isLight ? 'var(--accent-50)' : 'var(--accent-10)');
        const ring = palette ? `0 0 0 1px ${palette.glow}` : `0 0 0 1px var(--accent-500)20`;

        let itemStyle: React.CSSProperties = {};
        if (isActive) {
            itemStyle = {
                backgroundColor: activeBg,
                color: isLight ? c600 : (palette ? '#fff' : 'var(--accent-600)'),
                boxShadow: ring,
            };
        } else if (palette) {
            // Rest: text colored to match the section, bg transparent so the
            // section container's tint shows through.
            itemStyle = {
                color: isLight ? c600 : c400,
            };
        }

        return (
            <button
                key={item.id}
                onClick={() => {
                    onViewChange(item.id);
                    if (window.innerWidth < 1024) onClose();
                }}
                onMouseEnter={(e) => {
                    onPrefetchView?.(item.id);
                    showTip(e, item.label);
                    if (!isActive && palette) {
                        (e.currentTarget as HTMLButtonElement).style.backgroundColor = activeBg;
                    }
                }}
                onMouseLeave={(e) => {
                    hideTip();
                    if (!isActive && palette) {
                        (e.currentTarget as HTMLButtonElement).style.backgroundColor = '';
                    }
                }}
                onFocus={() => onPrefetchView?.(item.id)}
                aria-label={item.label}
                className={clsx(
                    'w-full flex items-center rounded-xl relative overflow-hidden',
                    'transition-[padding,color,background-color] duration-[350ms] ease-[cubic-bezier(0.4,0,0.2,1)]',
                    isCollapsed ? 'px-0 py-3 justify-center' : 'px-4 py-3 justify-between',
                    // Slate fallback only for items without a palette (Panel
                    // Control + super-admin tail).
                    !isActive && !palette && (isLight
                        ? 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'
                        : 'text-slate-400 hover:text-white hover:bg-white/5')
                )}
                style={itemStyle}
            >
                <div className={clsx(
                    'flex items-center shrink-0',
                    EASING,
                    isCollapsed ? 'gap-0' : 'gap-3'
                )}>
                    <div className="relative shrink-0">
                        <item.icon
                            size={20}
                            className="shrink-0 transition-colors duration-200"
                            style={(isActive || palette) ? { color: c500 } : {}}
                        />
                        {item.badge && isCollapsed ? (
                            <span
                                className="absolute -top-1 -right-1 inline-flex items-center justify-center text-[9px] font-semibold text-white rounded-full px-1 min-w-[14px] h-[14px]"
                                style={{ backgroundColor: c500 }}
                            >
                                {item.badge > 9 ? '9+' : item.badge}
                            </span>
                        ) : null}
                    </div>
                    <span
                        className={clsx(
                            'font-medium text-sm overflow-hidden whitespace-nowrap',
                            'transition-[max-width,opacity] duration-[350ms] ease-[cubic-bezier(0.4,0,0.2,1)]',
                            isCollapsed ? 'max-w-0 opacity-0' : 'max-w-[180px] opacity-100'
                        )}
                    >
                        {item.label}
                    </span>
                    {item.badge && !isCollapsed ? (
                        <span
                            className="ml-1 inline-flex items-center justify-center text-[10px] font-semibold text-white rounded-full px-1.5 min-w-[18px] h-[18px]"
                            style={{ backgroundColor: c500 }}
                        >
                            {item.badge > 99 ? '99+' : item.badge}
                        </span>
                    ) : null}
                </div>

                {/* Active dot (expanded) */}
                <div
                    className={clsx(
                        'w-1.5 h-1.5 rounded-full shrink-0',
                        'transition-[max-width,opacity] duration-[350ms] ease-[cubic-bezier(0.4,0,0.2,1)]',
                        isActive && !isCollapsed ? 'max-w-[6px] opacity-100' : 'max-w-0 opacity-0'
                    )}
                    style={{ backgroundColor: c500 }}
                />

                {/* Active bar (collapsed) */}
                <span
                    className={clsx(
                        'absolute right-0 top-1/2 -translate-y-1/2 w-1 rounded-l-full',
                        'transition-[height,opacity] duration-[350ms] ease-[cubic-bezier(0.4,0,0.2,1)]',
                        isActive && isCollapsed ? 'h-4 opacity-100' : 'h-0 opacity-0'
                    )}
                    style={{ backgroundColor: c500 }}
                />
            </button>
        );
    };

    const renderSection = (section: MenuSection) => {
        // When the sidebar itself is collapsed to icon width, ignore the
        // per-section toggle and show every item — headers are hidden in
        // that mode so there's no way to expand them otherwise.
        const isOpen = isCollapsed ? true : collapsedSections[section.id] !== true;
        const hasActiveChild = section.items.some(i => i.id === activeView);
        const palette = SECTION_PALETTE[section.id];
        // Section header text uses the palette's c600 (light) / c400 (dark)
        // at 80% opacity, bumping to 100% on hover — keeps the color
        // recognizable without shouting over the active item.
        const headerColor = palette ? (isLight ? palette.c600 : palette.c400) : (isLight ? '#94a3b8' : '#64748b');

        return (
            <div key={section.id} className="space-y-1">
                {!isCollapsed && (
                    <button
                        type="button"
                        onClick={() => toggleSection(section.id)}
                        className={clsx(
                            'w-full flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.14em]',
                            'transition-opacity opacity-80 hover:opacity-100',
                            palette ? 'mt-3 px-3 py-2 rounded-lg' : 'px-4 pt-4 pb-1.5 rounded-md'
                        )}
                        style={{
                            color: headerColor,
                            backgroundColor: palette ? (isLight ? palette.bgRestLight : palette.bgRestDark) : undefined,
                        }}
                    >
                        <span className="flex items-center gap-2">
                            {section.label}
                            {!isOpen && hasActiveChild && (
                                <span
                                    className="w-1.5 h-1.5 rounded-full"
                                    style={{ backgroundColor: palette?.c500 ?? 'var(--accent-500)' }}
                                />
                            )}
                        </span>
                        <ChevronDown
                            size={12}
                            className={clsx('transition-transform duration-200', isOpen ? '' : '-rotate-90')}
                        />
                    </button>
                )}

                <div className={clsx(
                    'overflow-hidden',
                    'transition-[max-height,opacity] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]',
                    isOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
                )}>
                    <div className="space-y-1">
                        {section.items.map(item => renderItem(item))}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
        <aside
            style={{ willChange: 'width' }}
            className={clsx(
                'fixed inset-y-0 left-0 h-dvh flex flex-col z-[60] overflow-hidden',
                EASING,
                'lg:sticky lg:translate-x-0',
                isCollapsed ? 'w-20' : 'w-72',
                isLight
                    ? 'bg-white border-r border-slate-200 shadow-[1px_0_20px_-5px_rgba(0,0,0,0.06)]'
                    : 'glass-panel border-r border-white/5',
                isOpen ? 'translate-x-0' : '-translate-x-full'
            )}
        >
            {/* Desktop collapse toggle lives in AppLayout as a sibling so the
                aside can keep overflow-hidden during the width transition. */}

            {/* ── Close btn (mobile) ── */}
            <button
                onClick={onClose}
                className={clsx(
                    'lg:hidden absolute top-8 right-6 p-2 rounded-xl',
                    isLight ? 'bg-slate-100 text-slate-500 hover:text-slate-900' : 'text-slate-400 hover:text-white'
                )}
                style={{ backgroundColor: isLight ? undefined : 'var(--accent-10)' }}
            >
                <X size={20} />
            </button>

            {/* ── Header: agency identity. Click → Configuración (the only
                entry point now that the menu item has been removed). ── */}
            <div className="p-4 pb-2 flex-shrink-0">
                <button
                    type="button"
                    onClick={() => {
                        onViewChange('settings');
                        if (window.innerWidth < 1024) onClose();
                    }}
                    onMouseEnter={(e) => showTip(e, 'Configuración')}
                    onMouseLeave={hideTip}
                    aria-label="Configuración"
                    className={clsx(
                        'flex items-center mb-4 overflow-hidden rounded-xl py-2 text-left w-full',
                        'transition-colors',
                        // gap/padding must go to 0 when collapsed: flex `gap`
                        // still applies while the name div animates to max-w-0,
                        // pushing the tile off-center and clipping its left edge.
                        isCollapsed ? 'justify-center gap-0 pl-0 pr-0' : 'justify-start gap-3 pl-0 pr-2',
                        isLight ? 'hover:bg-slate-100' : 'hover:bg-white/5',
                        EASING
                    )}>
                    {agency ? (
                        <div className={clsx(
                            'w-10 h-10 shrink-0 rounded-xl overflow-hidden flex items-center justify-center shadow-lg',
                            // Tile ground only for the logo fallback (it letterboxes);
                            // theme-aware so dark mode doesn't get white bars.
                            !agency.icon_url && agency.logo_url && (isLight ? 'bg-white' : 'bg-white/10'),
                        )}>
                            {agency.icon_url ? (
                                // The icon is 1:1 by contract — cover fills the tile edge to edge.
                                <img src={agency.icon_url} alt={agency.name} className="w-full h-full object-cover" />
                            ) : agency.logo_url ? (
                                // object-contain: a wide logo must letterbox, never crop.
                                <img src={agency.logo_url} alt={agency.name} className="w-full h-full object-contain" />
                            ) : (
                                <div
                                    className="w-full h-full flex items-center justify-center"
                                    style={{ background: 'linear-gradient(135deg, var(--accent-600), var(--accent-400))' }}
                                >
                                    <Building2 size={22} className="text-white" />
                                </div>
                            )}
                        </div>
                    ) : (
                        <div
                            className="w-10 h-10 shrink-0 rounded-xl flex items-center justify-center shadow-lg"
                            style={{ background: 'linear-gradient(135deg, var(--accent-600), var(--accent-400))', boxShadow: '0 4px 12px var(--accent-glow)' }}
                        >
                            <Sparkles size={22} className="text-white" />
                        </div>
                    )}
                    <div
                        className={clsx(
                            'overflow-hidden',
                            'transition-[max-width,max-height,opacity] duration-[350ms] ease-[cubic-bezier(0.4,0,0.2,1)]',
                            // max-h-0 when collapsed: agency.name uses break-words (so long
                            // names wrap nicely when expanded), which at max-w-0 wraps to ~1
                            // char/line and balloons height while invisible — max-h-0 clips it.
                            isCollapsed ? 'max-w-0 max-h-0 opacity-0' : 'max-w-[220px] opacity-100'
                        )}
                    >
                        {agency ? (
                            <>
                                <h1 className={clsx('text-base font-bold tracking-tight leading-tight break-words', isLight ? 'text-slate-900' : 'text-white')}>{agency.name}</h1>
                                <p className={clsx(
                                    'text-[10px] uppercase font-bold tracking-[0.18em] mt-0.5',
                                    profile?.role === 'super_admin' ? 'text-purple-500' :
                                    profile?.role === 'agency_admin' ? 'text-emerald-500' :
                                    'text-indigo-400'
                                )}>
                                    {profile?.role === 'super_admin' ? 'SUPER ADMIN' :
                                     profile?.role === 'agency_admin' ? 'AGENCY ADMIN' : 'AGENTE'}
                                </p>
                            </>
                        ) : (
                            <>
                                <h1 className={clsx('text-xl font-bold tracking-tight whitespace-nowrap', isLight ? 'text-slate-900' : 'text-white')}>Intercambio Inmobiliario</h1>
                                <p className="text-[10px] uppercase font-bold tracking-[0.2em] whitespace-nowrap" style={{ color: 'var(--accent-400)' }}>Tu Mejor Aliado</p>
                            </>
                        )}
                    </div>
                </button>
            </div>

            {/* ── Scrollable nav ── */}
            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 pb-2 scrollbar-thin">
                <nav className="space-y-1">
                    {topItems.map(renderItem)}

                    {sections.map(renderSection)}

                    {bottomItems.length > 0 && (
                        <>
                            <div className={clsx(
                                'my-3 border-t',
                                isLight ? 'border-slate-200/60' : 'border-white/5'
                            )} />
                            <div className="space-y-1">
                                {bottomItems.map(renderItem)}
                            </div>
                        </>
                    )}
                </nav>
            </div>

            {/* ── Bottom / profile section.
                paddingBottom uses env(safe-area-inset-bottom) so the
                "Cerrar Sesión" button always sits above iOS home indicator /
                bottom URL bar instead of being clipped by the aside's
                overflow-hidden when 100dvh briefly mismatches the visual
                viewport during browser-chrome transitions. ── */}
            <div
                className={clsx(
                    'flex-shrink-0 border-t overflow-hidden',
                    'transition-[padding] duration-[350ms] ease-[cubic-bezier(0.4,0,0.2,1)]',
                    isCollapsed ? 'p-4' : 'p-6',
                    isLight ? 'border-slate-100 bg-slate-50/50' : 'border-white/5'
                )}
                style={{ paddingBottom: `calc(${isCollapsed ? '1rem' : '1.5rem'} + env(safe-area-inset-bottom))` }}
            >

                {/* Logout */}
                <button
                    onClick={signOut}
                    onMouseEnter={(e) => showTip(e, 'Cerrar Sesión')}
                    onMouseLeave={hideTip}
                    aria-label="Cerrar Sesión"
                    className={clsx(
                        'w-full flex items-center rounded-xl text-slate-400 hover:text-rose-400 hover:bg-rose-500/5',
                        'transition-[padding,color,background-color] duration-[350ms] ease-[cubic-bezier(0.4,0,0.2,1)]',
                        isCollapsed ? 'justify-center px-0 py-3' : 'gap-3 px-4 py-3'
                    )}
                >
                    <LogOut size={20} className="shrink-0" />
                    <span className={clsx(
                        'font-medium text-sm overflow-hidden whitespace-nowrap',
                        'transition-[max-width,opacity] duration-[350ms] ease-[cubic-bezier(0.4,0,0.2,1)]',
                        isCollapsed ? 'max-w-0 opacity-0' : 'max-w-[180px] opacity-100'
                    )}>
                        Cerrar Sesión
                    </span>
                </button>
            </div>
        </aside>

        {/* Instant hover label for the collapsed icon rail. Portaled to <body>
            to escape the aside's overflow-hidden + transform containing block. */}
        {tip && createPortal(
            <div
                role="tooltip"
                style={{ position: 'fixed', top: tip.top, left: '5.5rem', transform: 'translateY(-50%)', zIndex: 80 }}
                className={clsx(
                    'pointer-events-none px-2.5 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap shadow-xl',
                    'animate-in fade-in duration-100',
                    isLight ? 'bg-slate-900 text-white' : 'bg-slate-800 text-white ring-1 ring-white/10'
                )}
            >
                {tip.label}
            </div>,
            document.body
        )}
        </>
    );
};
